import React, { useEffect, useRef, useState } from 'react'
import {
  BookOpen,
  Video,
  FileText,
  Download,
  GraduationCap,
  ChevronRight,
  PlayCircle,
} from 'lucide-react'
import { useWorkbooks } from '@/hooks/useWorkbooks'
import { CourseMaterials } from '@/lib/helpers'

function Workbooks() {
  const [courses, setCourses] = useState<CourseMaterials[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string>('')

  const getCourseTabKey = (course: CourseMaterials, index: number) =>
    `${course.courseName}__${index}`

  const { listWorkbooks } = useWorkbooks()
  const inFlight = useRef(false)
  const userId = 13052


  useEffect(() => {
    const controller = new AbortController()
    const run = async () => {
      if (inFlight.current) return
      inFlight.current = true
      try {
        setLoading(true)
        const result = await listWorkbooks()
        // const result1 = await getCourseMaterial(13052)
        // console.log('getCourseMaterial result:', result1)
        setCourses(result)
        if (result.length > 0) {
          setActiveTab(getCourseTabKey(result[0], 0))
        }
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          setError(e?.message || 'Could not load workbooks')
        }
      } finally {
        setLoading(false)
        inFlight.current = false
      }
    }
    run()
    return () => controller.abort()
  }, [])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px]">
        <div className="animate-spin rounded-full h-14 w-14 border-4 border-primary border-t-transparent mb-4" />
        <p className="text-lg font-semibold text-gray-800 animate-pulse">
          Loading course...
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded shadow-lg p-8 max-w-md">
          <div className="text-red-500 text-5xl mb-4 text-center">⚠️</div>
          <p className="text-center text-red-600 font-semibold">{error}</p>
        </div>
      </div>
    )
  }

  if (!courses.length) {
    return (
      <div className="h-[500px] bg-gray-50 flex items-center justify-center p-4">
        <div className="p-12 max-w-md text-center">
          <BookOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 text-lg">No course materials found.</p>
        </div>
      </div>
    )
  }

  const activeCourseIndex = activeTab
    ? Number(activeTab.split('__').pop())
    : NaN

  const activeCourse =
    !Number.isNaN(activeCourseIndex) && activeCourseIndex >= 0
      ? courses[activeCourseIndex]
      : undefined

  return (
    <div className="flex flex-col lg:flex-row md:container w-full gap-4 items-stretch">
      <div className="w-full flex flex-col gap-2">
        <div className="overflow-hidden">
          <div className={`min-h-[550px] p-0 overflow-hidden `}>
            <div className="flex-1 bg-gray-100">
              <div className="p-3 bg-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-primary rounded p-2">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  <h1 className="text-xl font-bold text-gray-900">
                    Course Materials
                  </h1>
                </div>

                <div className="flex lg:flex-row gap-2 flex-col w-full">
                  <div className="lg:h-[500px] h-[300px] overflow-y-auto lg:w-[33%] w-full border-[3px] rounded border-primary p-2 leading-none">
                    {courses.map((course, index) => {
                      const tabKey = getCourseTabKey(course, index)
                      const isActive = activeTab === tabKey

                      return (
                        <button
                          title={course.courseName}
                          key={tabKey}
                          onClick={() => setActiveTab(tabKey)}
                          className={`w-full text-left px-4 py-3 rounded transition-all duration-200 ${
                            isActive
                              ? 'bg-primary text-white shadow-md'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <BookOpen
                              className={`w-5 h-5 flex-shrink-0 ${
                                isActive ? 'text-white' : 'text-primary'
                              }`}
                            />

                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">
                                {course.courseName}
                              </div>
                              <div
                                className={`text-xs mt-0.5 ${
                                  isActive
                                    ? 'text-white'
                                    : 'text-gray-500'
                                }`}
                              >
                                {course.files.length === 0
                                  ? 'No items available'
                                  : `${course.files.length} ${course.files.length === 1 ? 'item' : 'items'} available`}
                              </div>
                            </div>

                            {isActive && (
                              <ChevronRight className="w-5 h-5 flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>

                  <div className="h-[500px] overflow-y-auto lg:w-[66%] w-full  flex-1 border-[3px] rounded border-primary p-2 leading-none">
                    {activeCourse ? (
                      <div className="p-3 md:p-4 lg:p-5">
                        <div className="max-w-4xl mx-auto">
                          <div className="mb-3">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                              {activeCourse.courseName}
                            </h2>
                            <p className="text-gray-600">
                              {activeCourse.files.length === 0
                                ? 'No items available'
                                : `${activeCourse.files.length} ${activeCourse.files.length === 1 ? 'item' : 'items'} available`}
                              {activeCourse.videoFiles.length > 0 &&
                                ' • Video recording included'}
                            </p>
                          </div>

                          <div className="mb-3">
                            <div className="flex items-center gap-2 mb-3">
                              <FileText className="w-5 h-5 text-gray-700" />
                              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                Workbooks & Materials
                              </h3>
                            </div>

                            {activeCourse.files.length > 0 ? (
                              <div className="space-y-3">
                                {activeCourse.files.map((file) => (
                                  <a
                                    key={file.url}
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block bg-white border border-gray-200 rounded p-2 hover:shadow-md hover:border-indigo-300 transition-all duration-200 group"
                                  >
                                    <div className="flex items-center gap-4">
                                      <div className="bg-indigo-100 rounded p-3">
                                        <Download className="w-5 h-5 text-indigo-600" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                                          {file.name}
                                        </div>
                                      </div>
                                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                                    </div>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <div className="bg-white border border-gray-200 rounded p-12 text-center">
                                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500">
                                  No workbooks available yet
                                </p>
                              </div>
                            )}
                          </div>

                          {activeCourse.videoFiles.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <Video className="w-5 h-5 text-gray-700" />
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                  Video Links
                                </h3>
                              </div>
                              <div className="space-y-3">
                                {activeCourse.videoFiles.map((file) => (
                                  <a
                                    key={file.url}
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block bg-white border border-gray-200 rounded p-2 hover:shadow-md hover:border-secondary transition-all duration-200 group"
                                  >
                                    <div className="flex items-center gap-4">
                                      <div className="bg-secondary/10 rounded p-3">
                                        <PlayCircle className="w-5 h-5 text-secondary" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 truncate group-hover:text-secondary transition-colors">
                                          {file.name}
                                        </div>
                                      </div>
                                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-secondary group-hover:translate-x-1 transition-all flex-shrink-0" />
                                    </div>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p>Select a course to view materials</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Workbooks
